import { useEffect, useRef, useState, type RefObject } from 'react';

import { useElementHover } from '@/hooks';

/** Shared idle timeout for overlay controls (radial pad, pause toggle) inside the video frame. */
const FRAME_CONTROLS_IDLE_MS = 3000;

type UseFrameRadialControlsVisibilityProps = {
  scopeRef: RefObject<HTMLElement | null>;
  /** Hide after this many ms without pointer movement inside the frame. */
  timeoutMs?: number;
  /** When true, the idle countdown is paused (e.g. while holding a sector). */
  pauseTimer?: boolean;
};

/**
 * Radial pad visibility: shown while the pointer is over the frame, hidden on leave
 * or after `timeoutMs` without movement inside the frame.
 */
export const useFrameRadialControlsVisibility = ({
  scopeRef,
  timeoutMs = FRAME_CONTROLS_IDLE_MS,
  pauseTimer = false,
}: UseFrameRadialControlsVisibilityProps): boolean => {
  const isFrameHovered = useElementHover(scopeRef);
  const [isAwake, setIsAwake] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const clearHideTimer = () => {
      if (hideTimerRef.current !== null) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };

    const scheduleHide = () => {
      clearHideTimer();
      hideTimerRef.current = setTimeout(() => {
        hideTimerRef.current = null;
        setIsAwake(false);
      }, timeoutMs);
    };

    /** Show the pad and restart (or skip) the idle countdown. */
    const wake = () => {
      setIsAwake(true);
      if (!pauseTimer) {
        scheduleHide();
      }
    };

    if (!isFrameHovered) {
      clearHideTimer();
      setIsAwake(false);
      return;
    }

    // Entering the frame shows the pad; movement inside keeps it awake.
    wake();

    const el = scopeRef.current;
    if (!el) {
      return clearHideTimer;
    }

    el.addEventListener('mousemove', wake);

    return () => {
      el.removeEventListener('mousemove', wake);
      clearHideTimer();
    };
  }, [isFrameHovered, pauseTimer, scopeRef, timeoutMs]);

  // `isAwake` is only true while hovered; no need to also gate on `isFrameHovered`.
  return isAwake;
};
